import React, { useState } from 'react';
import { motion } from 'framer-motion';

export const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5 },
};

// Two dark surfaces give sections rhythm without ever going back to light:
// 'base' is the page background, 'raised' is a faint panel for alternation.
export const Section = ({ children, className = '', tone = 'base', id }) => {
  const tones = {
    base: 'bg-[#0a0014]',
    raised: 'bg-white/[0.025] border-y border-white/5',
  };
  return (
    <section id={id} className={`py-16 md:py-24 ${tones[tone]} ${className}`}>
      <div className="container mx-auto px-[5%]">{children}</div>
    </section>
  );
};

export const SectionHeading = ({ eyebrow, title, subtitle, align = 'center', accent = 'purple' }) => (
  <div className={`max-w-2xl mb-12 md:mb-14 ${align === 'center' ? 'mx-auto text-center' : ''}`}>
    {eyebrow && (
      <span className={`text-sm font-bold tracking-widest uppercase ${accent === 'amber' ? 'text-amber-400' : 'text-purple-400'}`}>
        {eyebrow}
      </span>
    )}
    <h2 className="text-2xl md:text-4xl font-extrabold mt-3 text-white">{title}</h2>
    {subtitle && <p className="text-lg mt-4 leading-relaxed text-gray-400">{subtitle}</p>}
  </div>
);

export const PageHero = ({ eyebrow, title, subtitle, image, imageAlt, children }) => (
  <section className="relative overflow-hidden bg-[#0d0018] text-white pt-14 pb-16 md:pt-20 md:pb-24">
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-32 -left-24 w-[30rem] h-[30rem] rounded-full bg-purple-700/25 blur-3xl" />
      <div className="absolute -bottom-32 -right-24 w-[30rem] h-[30rem] rounded-full bg-fuchsia-600/15 blur-3xl" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {image && (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="lg:hidden absolute inset-0 w-full h-full object-contain opacity-10"
        />
      )}
    </div>
    {/* Above-the-fold content renders without an entrance animation: it is the LCP
        element, and gating it behind JS delays paint and leaves the hero blank if
        scripts are slow. */}
    <div className="relative container mx-auto px-[5%]">
      <div className={`grid items-center gap-10 ${image ? 'lg:grid-cols-2' : 'max-w-3xl mx-auto text-center'}`}>
        <div className={`flex flex-col ${image ? 'items-center text-center lg:items-start lg:text-left' : ''}`}>
          {eyebrow && (
            <span className="inline-block text-sm font-bold tracking-widest uppercase text-purple-300 mb-3">{eyebrow}</span>
          )}
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-5">{title}</h1>
          {subtitle && <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8">{subtitle}</p>}
          {children}
        </div>
        {image && (
          <div className="hidden lg:block">
            <img src={image} alt={imageAlt} width="820" height="546" className="w-full max-w-xl mx-auto drop-shadow-2xl" />
          </div>
        )}
      </div>
    </div>
  </section>
);

export const Card = ({ children, className = '', hover = false }) => (
  <motion.div
    {...fadeUp}
    className={`bg-white/[0.04] rounded-2xl border border-white/10 p-6 ${
      hover ? 'hover:bg-white/[0.06] hover:border-white/20 hover:-translate-y-0.5 transition-all duration-200' : ''
    } ${className}`}
  >
    {children}
  </motion.div>
);

export const IconBadge = ({ icon: Icon, tone = 'purple', className = '' }) => {
  const tones = {
    purple: 'bg-purple-500/10 text-purple-400',
    amber: 'bg-amber-500/10 text-amber-400',
  };
  return (
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone]} ${className}`}>
      <Icon className="w-5 h-5" />
    </div>
  );
};

export const Steps = ({ steps, tone = 'purple' }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
    {steps.map((step, i) => (
      <motion.div key={step.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="relative">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-white mb-4 ${
            tone === 'amber' ? 'bg-amber-500' : 'bg-purple-600'
          }`}
        >
          {i + 1}
        </div>
        <h3 className="font-bold text-white mb-2">{step.title}</h3>
        <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
      </motion.div>
    ))}
  </div>
);

const FaqItem = ({ q, a, isOpen, onToggle }) => (
  <div className="border-b border-white/10">
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-purple-400 transition-colors"
    >
      <span className="font-semibold text-white">{q}</span>
      <span className={`flex-shrink-0 text-purple-400 text-xl transition-transform ${isOpen ? 'rotate-45' : ''}`}>+</span>
    </button>
    {isOpen && (
      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden">
        <p className="pb-5 text-gray-400 leading-relaxed">{a}</p>
      </motion.div>
    )}
  </div>
);

// Renders an accordion plus matching FAQPage structured data for rich results.
export const Faq = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <div className="max-w-3xl mx-auto">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      {items.map((item, i) => (
        <FaqItem
          key={item.q}
          q={item.q}
          a={item.a}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
        />
      ))}
    </div>
  );
};

// Google Form iframes render blank for a moment while they load — this swaps
// in a skeleton pulse so that gap doesn't read as a broken/empty section.
export const EmbeddedForm = ({ src, height = 1800, title }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative rounded-xl overflow-hidden bg-white" style={{ minHeight: 240 }}>
      {!loaded && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute top-0 inset-x-0 h-[320px] flex flex-col items-center justify-center gap-5 bg-white rounded-xl"
        >
          <div className="relative w-14 h-14">
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'conic-gradient(from 0deg, #9333ea, #c084fc, transparent 65%)',
                WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
                mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-0 m-auto w-2.5 h-2.5 rounded-full bg-purple-600"
              animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
          <motion.p
            className="text-sm text-gray-400"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            Loading form…
          </motion.p>
        </motion.div>
      )}
      <motion.iframe
        src={src}
        width="640"
        height={height}
        marginHeight="0"
        marginWidth="0"
        title={title}
        onLoad={() => setLoaded(true)}
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        className="w-full rounded-xl bg-white border-0"
      >
        Loading…
      </motion.iframe>
    </div>
  );
};

export const CtaBand = ({ title, subtitle, children }) => (
  <section className="py-16 md:py-20 bg-gradient-to-br from-[#1a0033] to-[#3b0764] text-white border-t border-white/5">
    <div className="container mx-auto px-[5%] text-center">
      <h2 className="text-2xl md:text-3xl font-extrabold mb-3">{title}</h2>
      {subtitle && <p className="text-purple-100 max-w-xl mx-auto mb-8">{subtitle}</p>}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">{children}</div>
    </div>
  </section>
);
